package br.com.heyagenda.data.remote

import br.com.heyagenda.domain.util.SlugUtils
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import kotlinx.serialization.Serializable

@Serializable
data class ProfessionalProfileDto(
    val name: String = "",
    val studioName: String? = null,
    val specialty: String? = null,
    val email: String = "",
    val slug: String? = null,
    val themeAccentColor: String? = null,
    val instagramHandle: String? = null,
    val address: String? = null,
    val announcementBanner: String? = null,
    val totalVisits: Long = 0L,
    val phone: String? = null,
    val subscriptionTier: String? = "free",
    val subscriptionBillingCycle: String? = "annual",
    val subscriptionExpiresAt: Long? = null,
    val isFounderPromo: Boolean = true,
    val monthlyAppointmentsCount: Int = 0
)

interface FirestoreProfessionalDataSource {
    suspend fun saveProfile(uid: String, profile: ProfessionalProfileDto)
    suspend fun fetchProfile(uid: String): ProfessionalProfileDto?
    suspend fun isSlugAvailable(slug: String, excludeUid: String? = null): Boolean
    suspend fun generateUniqueSlug(baseName: String, uid: String): String
    suspend fun fetchProfileBySlug(slug: String): Pair<String, ProfessionalProfileDto>?
    suspend fun updateSlug(uid: String, slug: String): Result<Unit>
    suspend fun updateSubscriptionTier(uid: String, tier: String, cycle: String, expiresAt: Long? = null): Result<Unit>
    suspend fun updateWebSettings(
        uid: String,
        themeAccentColor: String?,
        instagramHandle: String?,
        address: String?,
        announcementBanner: String?
    ): Result<Unit>
    suspend fun recordPageView(uid: String, dateIso: String): Result<Unit>
    suspend fun fetchVisits(uid: String): Pair<Long, Long> // (todayVisits, totalVisits)
    suspend fun deleteProfile(uid: String): Result<Unit>
}

class FirestoreProfessionalDataSourceImpl : FirestoreProfessionalDataSource {

    private val firestore: FirebaseFirestore? by lazy {
        try {
            println(">>> [FirestoreInit] Initializing Firebase.firestore...")
            val fs = Firebase.firestore
            println(">>> [FirestoreInit SUCCESS] Firebase.firestore initialized successfully.")
            fs
        } catch (e: Throwable) {
            println(">>> [FirestoreInit ERROR] Failed to get Firebase.firestore: ${e.message}")
            e.printStackTrace()
            null
        }
    }

    override suspend fun saveProfile(uid: String, profile: ProfessionalProfileDto) {
        val instance = firestore
        if (instance == null) {
            println(">>> [FirestoreSave ERROR] Cannot save profile for $uid because firestore instance is NULL!")
            return
        }

        println(">>> [FirestoreSave START] Writing to professionals/$uid: $profile")
        try {
            kotlinx.coroutines.withTimeout(6000L) {
                try {
                    instance.collection("professionals").document(uid).set(profile)
                    println(">>> [FirestoreSave SUCCESS] Profile written successfully to professionals/$uid")
                } catch (e: Throwable) {
                    println(">>> [FirestoreSave DTO ERROR] Failed with DTO set, retrying with Map: ${e.message}")
                    instance.collection("professionals").document(uid).set(
                        mapOf(
                            "name" to profile.name,
                            "studioName" to (profile.studioName ?: ""),
                            "specialty" to (profile.specialty ?: ""),
                            "email" to profile.email,
                            "slug" to (profile.slug ?: ""),
                            "themeAccentColor" to (profile.themeAccentColor ?: ""),
                            "instagramHandle" to (profile.instagramHandle ?: ""),
                            "address" to (profile.address ?: ""),
                            "announcementBanner" to (profile.announcementBanner ?: ""),
                            "totalVisits" to profile.totalVisits,
                            "phone" to (profile.phone ?: ""),
                            "subscriptionTier" to (profile.subscriptionTier ?: "free"),
                            "subscriptionBillingCycle" to (profile.subscriptionBillingCycle ?: "annual"),
                            "isFounderPromo" to profile.isFounderPromo,
                            "monthlyAppointmentsCount" to profile.monthlyAppointmentsCount
                        )
                    )
                    println(">>> [FirestoreSave MAP SUCCESS] Profile written successfully with Map to professionals/$uid")
                }
            }
        } catch (ex: Throwable) {
            println(">>> [FirestoreSave TIMEOUT/ERROR] Failed or timed out writing profile: ${ex.message}")
            throw ex
        }
    }

    override suspend fun fetchProfile(uid: String): ProfessionalProfileDto? {
        val instance = firestore
        if (instance == null) {
            println(">>> [FirestoreFetch ERROR] Cannot fetch profile for $uid because firestore instance is NULL!")
            return null
        }

        println(">>> [FirestoreFetch START] Fetching professionals/$uid...")
        return try {
            kotlinx.coroutines.withTimeoutOrNull(4000L) {
                try {
                    val doc = instance.collection("professionals").document(uid).get()
                    if (doc.exists) {
                        parseProfileDto(doc)
                    } else {
                        println(">>> [FirestoreFetch NOT FOUND] Document professionals/$uid does NOT exist!")
                        null
                    }
                } catch (e: Throwable) {
                    println(">>> [FirestoreFetch ERROR] Failed to fetch professionals/$uid: ${e.message}")
                    null
                }
            }
        } catch (ex: Throwable) {
            println(">>> [FirestoreFetch TIMEOUT] Timed out fetching profile for $uid: ${ex.message}")
            null
        }
    }

    override suspend fun isSlugAvailable(slug: String, excludeUid: String?): Boolean {
        val instance = firestore ?: return true
        val cleanSlug = SlugUtils.toSlug(slug)
        return try {
            kotlinx.coroutines.withTimeoutOrNull(3000L) {
                try {
                    val querySnapshot = instance.collection("professionals")
                        .where { "slug" equalTo cleanSlug }
                        .get()
                    val matching = querySnapshot.documents.filter { it.id != excludeUid }
                    matching.isEmpty()
                } catch (e: Throwable) {
                    println(">>> [FirestoreSlug ERROR] isSlugAvailable: ${e.message}")
                    true
                }
            } ?: true
        } catch (e: Throwable) {
            true
        }
    }

    override suspend fun generateUniqueSlug(baseName: String, uid: String): String {
        val base = SlugUtils.toSlug(baseName)
        if (isSlugAvailable(base, uid)) {
            return base
        }
        repeat(10) {
            val candidate = SlugUtils.appendRandomSuffix(base)
            if (isSlugAvailable(candidate, uid)) {
                return candidate
            }
        }
        return "$base-${uid.take(5).lowercase()}"
    }

    override suspend fun fetchProfileBySlug(slug: String): Pair<String, ProfessionalProfileDto>? {
        val instance = firestore
        if (instance == null) {
            println(">>> [FirestoreSlug ERROR] fetchProfileBySlug: Firestore is NULL")
            return null
        }
        val cleanSlug = SlugUtils.toSlug(slug)
        println(">>> [FirestoreSlug] Searching profile for slug: '$cleanSlug' (original: '$slug')")

        // 1. Search by slug property (clean slug)
        try {
            val querySnapshot = instance.collection("professionals")
                .where { "slug" equalTo cleanSlug }
                .get()
            val matchDoc = querySnapshot.documents.firstOrNull()
            if (matchDoc != null) {
                println(">>> [FirestoreSlug MATCH] Found document by clean slug: ${matchDoc.id}")
                val profile = parseProfileDto(matchDoc)
                if (profile != null) {
                    return Pair(matchDoc.id, profile)
                }
            }
        } catch (e: Throwable) {
            println(">>> [FirestoreSlug ERROR] Query by clean slug failed: ${e.message}")
        }

        // 2. Search by raw slug property if different
        if (slug != cleanSlug) {
            try {
                val querySnapshot = instance.collection("professionals")
                    .where { "slug" equalTo slug }
                    .get()
                val matchDoc = querySnapshot.documents.firstOrNull()
                if (matchDoc != null) {
                    println(">>> [FirestoreSlug MATCH] Found document by raw slug: ${matchDoc.id}")
                    val profile = parseProfileDto(matchDoc)
                    if (profile != null) {
                        return Pair(matchDoc.id, profile)
                    }
                }
            } catch (e: Throwable) {
                println(">>> [FirestoreSlug ERROR] Query by raw slug failed: ${e.message}")
            }
        }

        // 3. Direct fallback to document ID (raw slug or cleanSlug)
        for (candidateId in listOf(slug, cleanSlug).distinct()) {
            try {
                val directDoc = instance.collection("professionals").document(candidateId).get()
                if (directDoc.exists) {
                    println(">>> [FirestoreSlug MATCH] Found document by ID: $candidateId")
                    val profile = parseProfileDto(directDoc)
                    if (profile != null) {
                        return Pair(directDoc.id, profile)
                    }
                }
            } catch (e: Throwable) {
                println(">>> [FirestoreSlug ERROR] Direct doc get '$candidateId' failed: ${e.message}")
            }
        }

        // 4. Collection-wide scan fallback (in case studioName/name/slug matches case-insensitively)
        try {
            val allDocs = instance.collection("professionals").get()
            val match = allDocs.documents.firstOrNull { doc ->
                val docSlug = try { doc.get<String?>("slug") } catch (_: Throwable) { null }
                val docStudio = try { doc.get<String?>("studioName") } catch (_: Throwable) { null }
                val docName = try { doc.get<String?>("name") } catch (_: Throwable) { null }

                doc.id.equals(cleanSlug, ignoreCase = true) ||
                doc.id.equals(slug, ignoreCase = true) ||
                (!docSlug.isNullOrBlank() && (docSlug.equals(cleanSlug, ignoreCase = true) || docSlug.equals(slug, ignoreCase = true))) ||
                (!docStudio.isNullOrBlank() && (SlugUtils.toSlug(docStudio).equals(cleanSlug, ignoreCase = true) || docStudio.equals(slug, ignoreCase = true))) ||
                (!docName.isNullOrBlank() && (SlugUtils.toSlug(docName).equals(cleanSlug, ignoreCase = true) || docName.equals(slug, ignoreCase = true)))
            }

            if (match != null) {
                println(">>> [FirestoreSlug MATCH] Found document via collection scan: ${match.id}")
                val profile = parseProfileDto(match)
                if (profile != null) {
                    return Pair(match.id, profile)
                }
            }
        } catch (e: Throwable) {
            println(">>> [FirestoreSlug ERROR] Collection scan failed: ${e.message}")
        }

        return null
    }

    override suspend fun updateSlug(uid: String, slug: String): Result<Unit> {
        val instance = firestore ?: return Result.failure(Exception("Firestore não inicializado"))
        val cleanSlug = SlugUtils.toSlug(slug)
        if (!isSlugAvailable(cleanSlug, uid)) {
            return Result.failure(Exception("Este link já está sendo utilizado por outro estúdio."))
        }

        return try {
            val existing = fetchProfile(uid) ?: ProfessionalProfileDto()
            saveProfile(uid, existing.copy(slug = cleanSlug))
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun updateSubscriptionTier(uid: String, tier: String, cycle: String, expiresAt: Long?): Result<Unit> {
        val instance = firestore ?: return Result.failure(Exception("Firestore não inicializado"))
        return try {
            val existing = fetchProfile(uid) ?: ProfessionalProfileDto()
            saveProfile(
                uid,
                existing.copy(
                    subscriptionTier = tier,
                    subscriptionBillingCycle = cycle,
                    subscriptionExpiresAt = expiresAt ?: existing.subscriptionExpiresAt
                )
            )
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun updateWebSettings(
        uid: String,
        themeAccentColor: String?,
        instagramHandle: String?,
        address: String?,
        announcementBanner: String?
    ): Result<Unit> {
        val instance = firestore ?: return Result.failure(Exception("Firestore não inicializado"))
        return try {
            val existing = fetchProfile(uid) ?: ProfessionalProfileDto()
            saveProfile(
                uid,
                existing.copy(
                    themeAccentColor = themeAccentColor,
                    instagramHandle = instagramHandle,
                    address = address,
                    announcementBanner = announcementBanner
                )
            )
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun recordPageView(uid: String, dateIso: String): Result<Unit> {
        val instance = firestore ?: return Result.failure(Exception("Firestore não inicializado"))
        return try {
            val docId = "${dateIso}_${kotlin.random.Random.nextLong().toString(16)}"
            instance.collection("professionals")
                .document(uid)
                .collection("pageViews")
                .document(docId)
                .set(mapOf("date" to dateIso))
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [RecordPageView ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun fetchVisits(uid: String): Pair<Long, Long> {
        val instance = firestore ?: return Pair(0L, 0L)
        return try {
            val views = instance.collection("professionals")
                .document(uid)
                .collection("pageViews")
                .get()

            val todayStr = br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()

            var todayCount = 0L
            var totalCount = 0L

            for (doc in views.documents) {
                totalCount++
                val dateVal = try { doc.get<String?>("date") } catch (_: Throwable) { null }
                if (dateVal == todayStr) {
                    todayCount++
                }
            }
            Pair(todayCount, totalCount)
        } catch (e: Throwable) {
            println(">>> [FetchVisits ERROR] ${e.message}")
            Pair(0L, 0L)
        }
    }

    private fun parseProfileDto(doc: DocumentSnapshot): ProfessionalProfileDto? {
        val name: String? = try { doc.get<String?>("name") } catch (_: Throwable) { null }
            ?: try { doc.get<String?>("nome") } catch (_: Throwable) { null }

        val studioName: String? = try { doc.get<String?>("studioName") } catch (_: Throwable) { null }
            ?: try { doc.get<String?>("nomeStudio") } catch (_: Throwable) { null }
            ?: try { doc.get<String?>("studio") } catch (_: Throwable) { null }

        val specialty: String? = try { doc.get<String?>("specialty") } catch (_: Throwable) { null }
            ?: try { doc.get<String?>("especialidade") } catch (_: Throwable) { null }

        val email: String? = try { doc.get<String?>("email") } catch (_: Throwable) { null }
        val slug: String? = try { doc.get<String?>("slug") } catch (_: Throwable) { null }
        val themeAccentColor: String? = try { doc.get<String?>("themeAccentColor") } catch (_: Throwable) { null }
        val instagramHandle: String? = try { doc.get<String?>("instagramHandle") } catch (_: Throwable) { null }
        val address: String? = try { doc.get<String?>("address") } catch (_: Throwable) { null }
        val announcementBanner: String? = try { doc.get<String?>("announcementBanner") } catch (_: Throwable) { null }
        val totalVisits: Long = try { doc.get<Long>("totalVisits") } catch (_: Throwable) { 0L }
        val phone: String? = try { doc.get<String?>("phone") } catch (_: Throwable) { null }
            ?: try { doc.get<String?>("telefone") } catch (_: Throwable) { null }
            ?: try { doc.get<String?>("whatsapp") } catch (_: Throwable) { null }

        val subscriptionTier: String? = try { doc.get<String?>("subscriptionTier") } catch (_: Throwable) { null }
        val subscriptionBillingCycle: String? = try { doc.get<String?>("subscriptionBillingCycle") } catch (_: Throwable) { null }
        val subscriptionExpiresAt: Long? = try { doc.get<Long?>("subscriptionExpiresAt") } catch (_: Throwable) { null }
        val isFounderPromo: Boolean = try { doc.get<Boolean>("isFounderPromo") } catch (_: Throwable) { true }
        val monthlyAppointmentsCount: Int = try { doc.get<Int>("monthlyAppointmentsCount") } catch (_: Throwable) { 0 }

        return if (!name.isNullOrBlank() || !studioName.isNullOrBlank()) {
            ProfessionalProfileDto(
                name = name.orEmpty(),
                studioName = studioName,
                specialty = specialty,
                email = email.orEmpty(),
                slug = slug,
                themeAccentColor = themeAccentColor,
                instagramHandle = instagramHandle,
                address = address,
                announcementBanner = announcementBanner,
                totalVisits = totalVisits,
                phone = phone,
                subscriptionTier = subscriptionTier ?: "free",
                subscriptionBillingCycle = subscriptionBillingCycle ?: "annual",
                subscriptionExpiresAt = subscriptionExpiresAt,
                isFounderPromo = isFounderPromo,
                monthlyAppointmentsCount = monthlyAppointmentsCount
            )
        } else {
            try {
                doc.data<ProfessionalProfileDto>()
            } catch (_: Throwable) {
                null
            }
        }
    }

    override suspend fun deleteProfile(uid: String): Result<Unit> {
        val instance = firestore ?: return Result.failure(Exception("Firestore não inicializado"))
        return try {
            kotlinx.coroutines.withTimeout(30_000L) {
                val profDoc = instance.collection("professionals").document(uid)
                val subcollections = listOf(
                    "appointments",
                    "services",
                    "clients",
                    "expenses",
                    "availability",
                    "pageViews"
                )

                for (subName in subcollections) {
                    try {
                        val snapshot = profDoc.collection(subName).get()
                        for (doc in snapshot.documents) {
                            try {
                                profDoc.collection(subName).document(doc.id).delete()
                            } catch (docEx: Throwable) {
                                println(">>> [DeleteProfile WARN] Falha ao deletar $subName/${doc.id}: ${docEx.message}")
                            }
                        }
                    } catch (subEx: Throwable) {
                        println(">>> [DeleteProfile WARN] Falha ao listar subcoleção $subName: ${subEx.message}")
                    }
                }

                try {
                    profDoc.collection("availability").document("schedule").delete()
                } catch (_: Throwable) {}

                profDoc.delete()
                println(">>> [DeleteProfile SUCCESS] Documento raiz e todas as subcoleções deletadas para $uid")
            }
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [DeleteProfile ERROR] ${e.message}")
            Result.failure(e)
        }
    }
}
